import { useId, useRef, useState, type ReactNode } from 'react'
import type { FieldErrors } from 'react-hook-form'

import { FieldShell } from '@atoms/form/FieldShell'
import * as SelectDropdown from '@atoms/form/SelectDropdown'
import {
  Combobox,
  ComboboxCheckItem,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxTrigger,
} from '@components/atoms/Combobox'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { useFormField } from '@hooks/useFormField'
import { scopedTranslation } from '@frontend/utils/I18n'

const t = scopedTranslation('components.multiselect_dropdown')
const MAX_VISIBLE_PILLS = 6

export type MultiSelectFieldOption = {
  value: string
  label: string
  description?: ReactNode
  badge?: ReactNode
  disabled?: boolean
  // Sub-options, revealed indented while the parent option is selected.
  children?: MultiSelectFieldOption[]
  hidden?: boolean
}

type MultiSelectFieldProps = {
  name?: string
  options: MultiSelectFieldOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  label: ReactNode
  placeholder: ReactNode
  searchPlaceholder: string
  noResultsText: string
  description?: string
  errors?: FieldErrors
  required?: boolean
  disabled?: boolean
  hideLabel?: boolean
  listHeader?: ReactNode
  showSelectAll?: boolean
  showSelectedPills?: boolean
  selectedLabel?: (selected: MultiSelectFieldOption[]) => ReactNode
}

export function MultiSelectField({
  name,
  options,
  value,
  onValueChange,
  label,
  placeholder,
  searchPlaceholder,
  noResultsText,
  description,
  errors,
  required,
  disabled = false,
  hideLabel,
  listHeader,
  showSelectAll = false,
  showSelectedPills = true,
  selectedLabel,
}: MultiSelectFieldProps) {
  const [search, setSearch] = useState('')
  const [showAllPills, setShowAllPills] = useState(false)
  const triggerId = useId()
  const labelId = `${triggerId}-label`
  const selectAllId = `${triggerId}-select-all`
  const field = useFormField(name ?? triggerId, errors, required, description)
  const portalContainerRef = useRef<HTMLDivElement>(null)
  const selectedIds = new Set(value)
  const childrenOf = (option: MultiSelectFieldOption) => option.children ?? []
  const selectedOptions = options.filter((option) => selectedIds.has(option.value))
  const selectableOptions = options.filter((option) => !option.disabled && !option.hidden)
  const allSelected = selectableOptions.length > 0 && selectableOptions.every((option) => selectedIds.has(option.value))
  const comboboxValue = allSelected ? [...value, selectAllId] : value
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const filteredOptions = options.filter(
    (option) => !option.hidden && option.label.toLocaleLowerCase().includes(normalizedSearch)
  )
  const visiblePills = showAllPills ? selectedOptions : selectedOptions.slice(0, MAX_VISIBLE_PILLS)
  const triggerLabel =
    selectedOptions.length === 0
      ? undefined
      : selectedLabel
        ? selectedLabel(selectedOptions)
        : t('selected_count', { count: selectedOptions.length })

  // Children only survive while their parent does, so deselecting a parent drops them.
  const applySelection = (nextIds: Set<string>, nextChildIds: Set<string> = selectedIds) =>
    onValueChange(
      options.flatMap((option) =>
        nextIds.has(option.value)
          ? [
              option.value,
              ...childrenOf(option)
                .map((child) => child.value)
                .filter((id) => nextChildIds.has(id)),
            ]
          : []
      )
    )

  const lockedIds = () => new Set(selectedOptions.filter((option) => option.disabled).map((option) => option.value))

  const handleValueChange = (nextValue: string[]) => {
    const nextIds = new Set(nextValue)

    if (nextIds.has(selectAllId) !== allSelected) {
      if (allSelected) return applySelection(lockedIds())

      return applySelection(new Set([...selectedIds, ...selectableOptions.map((option) => option.value)]))
    }

    applySelection(nextIds, nextIds)
  }

  const renderOption = (option: MultiSelectFieldOption, parent?: MultiSelectFieldOption) => {
    const optionId = `${triggerId}-option-${encodeURIComponent(option.value)}`
    return (
      <ComboboxCheckItem
        key={option.value}
        value={option.value}
        selected={selectedIds.has(option.value)}
        disabled={option.disabled}
        aria-labelledby={`${optionId}-label`}
        aria-describedby={option.description ? `${optionId}-description` : undefined}
        indicatorClassName="tw:translate-y-1"
        className={cn(
          'tw:items-start tw:gap-4 tw:rounded-none tw:py-2',
          parent ? 'tw:pl-12 tw:pr-4 tw:text-sm' : 'tw:px-4 tw:text-base'
        )}
      >
        <span className="tw:flex tw:flex-col">
          <span id={`${optionId}-label`} className="tw:flex tw:items-center tw:gap-2">
            {parent && <span className="tw:sr-only">{parent.label}: </span>}
            {option.label}
            {option.badge && <span className="tw:shrink-0 tw:whitespace-nowrap">{option.badge}</span>}
          </span>
          {option.description && (
            <span id={`${optionId}-description`} className="tw:text-sm tw:text-gray-500">
              {option.description}
            </span>
          )}
        </span>
      </ComboboxCheckItem>
    )
  }

  return (
    <Combobox
      multiple
      value={comboboxValue}
      onValueChange={handleValueChange}
      onInputValueChange={setSearch}
      onOpenChange={(open) => {
        if (!open) {
          setSearch('')
          setShowAllPills(false)
        }
      }}
      filter={null}
      disabled={disabled}
    >
      <FieldShell
        ref={portalContainerRef}
        field={field}
        label={label}
        labelId={labelId}
        hideLabel={hideLabel}
        description={description}
      >
        <ComboboxTrigger
          id={triggerId}
          aria-labelledby={labelId}
          aria-describedby={field.ariaDescribedBy}
          aria-invalid={field.ariaInvalid || undefined}
          aria-required={required || undefined}
          data-placeholder={triggerLabel ? undefined : ''}
          render={<button type="button" className={SelectDropdown.triggerClassName} />}
        >
          <span>{triggerLabel ?? placeholder}</span>
          <SelectDropdown.TriggerChevron />
        </ComboboxTrigger>
      </FieldShell>

      <ComboboxContent
        portalContainer={portalContainerRef}
        // Flip above the trigger when there is more room there: nested options make the
        // list tall, and a field low in a dialog has little space below it.
        collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'none' }}
        className="tw:flex tw:w-(--anchor-width) tw:flex-col"
      >
        {showSelectedPills && selectedOptions.length > 0 && (
          <div className="tw:flex tw:flex-wrap tw:gap-2 tw:border-b tw:border-gray-200 tw:px-4 tw:py-3">
            {visiblePills.map((option) => (
              <span
                key={option.value}
                className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-md tw:border tw:border-gray-200 tw:bg-gray-100 tw:py-1 tw:pl-2 tw:pr-1 tw:text-sm tw:text-gray-900"
              >
                {option.label}
                {!option.disabled && (
                  <button
                    type="button"
                    aria-label={t('remove', { label: option.label })}
                    className="tw:flex tw:h-5 tw:w-5 tw:items-center tw:justify-center tw:rounded tw:hover:bg-gray-200 tw:focus:outline-hidden tw:focus:ring-2 tw:focus:ring-primary-400"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      applySelection(new Set([...selectedIds].filter((id) => id !== option.value)))
                    }}
                  >
                    <Icon type="x-close" width={12} height={12} />
                  </button>
                )}
              </span>
            ))}
            {selectedOptions.length > MAX_VISIBLE_PILLS && !showAllPills && (
              <button
                type="button"
                aria-label={t('show_all')}
                className="tw:rounded-md tw:border tw:border-gray-200 tw:bg-gray-100 tw:px-2 tw:py-1 tw:text-sm tw:hover:bg-gray-200 tw:focus:outline-hidden tw:focus:ring-2 tw:focus:ring-primary-400"
                onClick={() => setShowAllPills(true)}
              >
                …
              </button>
            )}
            {selectedOptions.some((option) => !option.disabled) && (
              <button
                type="button"
                className="tw:ml-auto tw:self-center tw:text-sm tw:text-primary-600 tw:hover:underline tw:focus:outline-hidden tw:focus:ring-2 tw:focus:ring-primary-400"
                onClick={() => applySelection(lockedIds())}
              >
                {t('clear_all')}
              </button>
            )}
          </div>
        )}

        <ComboboxInput placeholder={searchPlaceholder} className="tw:px-4" />

        {listHeader}

        <ComboboxList className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-0">
          {filteredOptions.length === 0 && (
            <p className="tw:px-4 tw:py-6 tw:text-center tw:text-sm tw:text-gray-500">{noResultsText}</p>
          )}
          {showSelectAll && filteredOptions.length > 0 && (
            <ComboboxCheckItem
              value={selectAllId}
              selected={allSelected}
              indicatorClassName="tw:translate-y-1"
              className="tw:items-start tw:gap-4 tw:rounded-none tw:px-4 tw:py-2 tw:text-base"
            >
              {t('select_all')}
            </ComboboxCheckItem>
          )}
          {filteredOptions.flatMap((option) => [
            renderOption(option),
            ...(selectedIds.has(option.value) ? childrenOf(option).map((child) => renderOption(child, option)) : []),
          ])}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
